import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as amplitude from "@amplitude/analytics-browser";
import { useNavigate } from 'react-router-dom';
import { useGlobalStore } from '@app/dashboard/store/global';
import {
  Input,
  Dropdown,
  Button,
  DropdownButton,
  DropdownMenuItem,
  DropdownMenu,
  Label,
  Checkbox
} from '@unified-checkout/design-system';
import {
  optionalButHasValidation,
  required,
  emailValidation,
  isFormValid,
  passwordMatchValidation,
  passwordValidations,
  passwordValidationRules
} from '@app/dashboard/utils/formValidations';
import { COUNTRIES } from '@app/dashboard/constants/countries';
import { THREE_SECONDS } from '@app/dashboard/constants/shared';
import type { IField } from '@app/dashboard/types/outerLayout/common';
import type { IRegisterForm } from '@app/dashboard/types/outerLayout/register';
import { useEnterKeyPress } from '@app/dashboard/hooks/useEnterKeyPress';
import { leadsRegister, register } from '@app/dashboard/services/outer_layout/register';
import { Spinner } from '@app/dashboard/assets/icons';
import {
  country,
  getRelatedLabel,
  mccs,
  pakistanProvinceBasedCities,
  pakistanProvinces,
  preferredLanguages,
  products
} from '../utils';
import { handleAfterLogin } from '../../Login/utils';

import styles from "../register.module.scss";

const SUBMIT_BTN_ID = 'register-form-submit-btn';

const FieldNames = {
  FIRST_NAME: "first_name",
  LAST_NAME: "last_name",
  EMAIL: "email",
  BUSINESS_NAME: "business_name",
  PREFERRED_LANGUAGE: "preferred_language",
  PROVINCE: "province",
  CITY: "city",
  BUSINESS_TYPE: "business_type",
  PRODUCT: "product",
  PASSWORD: "password",
  CONFIRM_PASSWORD: "confirm_password",
};

const RegisterForm = ({
  validateField,
  loading,
  setLoading,
  redirectToLogin,
  setCurrentStep,
  STEPS,
  leadToken
}: IRegisterForm
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setIsAuthenticated } = useGlobalStore();

  useEnterKeyPress(SUBMIT_BTN_ID);

  const fieldsInitialState = {

    ...(leadToken ?
      {}
      :
      {
        [FieldNames.FIRST_NAME]: {
          value: '',
          isValid: undefined,
          errorMsg: '',
          validations: [required]
        },
        [FieldNames.LAST_NAME]: {
          value: '',
          isValid: undefined,
          errorMsg: '',
          validations: [required]
        },
        [FieldNames.EMAIL]: {
          value: '',
          isValid: true,
          errorMsg: '',
          validations: [optionalButHasValidation(emailValidation)]
        },
        [FieldNames.BUSINESS_NAME]: {
          value: '',
          isValid: undefined,
          errorMsg: '',
          validations: [required]
        },
        [FieldNames.PREFERRED_LANGUAGE]: {
          value: '',
          isValid: undefined,
          errorMsg: '',
          validations: [required]
        },
        ...(country === COUNTRIES.PAK ?
          {
            [FieldNames.PROVINCE]: {
              value: '',
              isValid: undefined,
              errorMsg: '',
              validations: [required]
            },
            [FieldNames.CITY]: {
              value: '',
              isValid: undefined,
              errorMsg: '',
              validations: [required]
            },
            [FieldNames.BUSINESS_TYPE]: {
              value: '',
              isValid: undefined,
              errorMsg: '',
              validations: [required]
            },
            [FieldNames.PRODUCT]: {
              value: '',
              isValid: undefined,
              errorMsg: '',
              validations: [required]
            }
          }
          :
          {}
        ),
      }
    ),
    [FieldNames.PASSWORD]: {
      value: undefined,
      isValid: undefined,
      errorMsg: '',
      validations: passwordValidations
    },
    [FieldNames.CONFIRM_PASSWORD]: {
      value: undefined,
      isValid: undefined,
      errorMsg: '',
      validations: [required, passwordMatchValidation]
    },
  };

  const [fields, setFields] = useState<IField>(fieldsInitialState);
  const [showPasswordRules, setShowPasswordRules] = useState(false);
  const [serverErrMsg, setServerErrMsg] = useState('');

  useEffect(() => {
    const passwordValue = fields[FieldNames.PASSWORD].value;
    const confirmPasswordValue = fields[FieldNames.CONFIRM_PASSWORD].value;

    typeof passwordValue === 'string' &&
      (validateField(fields, setFields, FieldNames.PASSWORD, passwordValue), setShowPasswordRules(true));

    typeof confirmPasswordValue === 'string' && validateField(fields, setFields, FieldNames.CONFIRM_PASSWORD, confirmPasswordValue);

  }, [fields[FieldNames.PASSWORD].value]);

  useEffect(() => {
    const confirmPasswordValue = fields[FieldNames.CONFIRM_PASSWORD].value;
    typeof confirmPasswordValue === 'string' && validateField(fields, setFields, FieldNames.CONFIRM_PASSWORD, confirmPasswordValue);

  }, [fields[FieldNames.CONFIRM_PASSWORD].value]);

  const onProductsChange = () => {
    const checkedProducts = document.querySelectorAll('input[name="products"]:checked');
    const names = Array.from(checkedProducts).map((product: any) => (product.id));

    onChangeHandler(FieldNames.PRODUCT, JSON.stringify(names));
  };

  const onChangeHandler = (fieldName: string, value: string) => {
    setServerErrMsg("");

    setFields(prevState => {
      return ({
        ...prevState,
        [fieldName]: {
          ...prevState[fieldName],
          value: value,
        }
      })
    });

    validateField(fields, setFields, fieldName, value);
  };

  const handleSubmit = () => {
    setServerErrMsg("");
    setLoading(true);

    const args = {
      "firstName": fields[FieldNames.FIRST_NAME].value as string,
      "lastName": fields[FieldNames.LAST_NAME].value as string,
      "email": fields[FieldNames.EMAIL].value as string,
      "businessName": fields[FieldNames.BUSINESS_NAME].value as string,
      "preferredLanguage": fields[FieldNames.PREFERRED_LANGUAGE].value as string,
      ...(country === COUNTRIES.PAK ?
        {
          "city": fields[FieldNames.CITY].value as string,
          "businessType": fields[FieldNames.BUSINESS_TYPE].value as string,
          "products": fields[FieldNames.PRODUCT].value as string
        }
        :
        {}
      ),
      "password": fields[FieldNames.PASSWORD].value as string
    };

    register(args)
      .then(async (response) => {
        amplitude.track('sign_up_details');

        // await handleAfterLogin(
        //   response,
        //   navigate,
        //   setServerErrMsg
        // );

        setCurrentStep(STEPS.SUCCESSFUL_REGISTER);

        setTimeout(() => {
            redirectToLogin();
          // setIsAuthenticated(true);
        }, THREE_SECONDS);
      })
      .catch((error) => {
        const message = error.response?.data?.message;
        const otpExpired = error.response?.data?.otp_expired;

        setServerErrMsg(message || t('errors.somethingWentWrong'));

        otpExpired && setTimeout(() => {
          setCurrentStep(STEPS.PHONE_NUMBER_VERIFICATION);
        }, THREE_SECONDS);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleLeadsSubmit = () => {
    setServerErrMsg("");
    setLoading(true);

    const password = fields[FieldNames.PASSWORD].value as string;

    leadsRegister(password, leadToken)
      .then(() => {
        setCurrentStep(STEPS.SUCCESSFUL_REGISTER);

        setTimeout(() => {
          redirectToLogin();
        }, THREE_SECONDS);
      })
      .catch((error) => {
        const message = error.response?.data?.message;

        setServerErrMsg(message || t('errors.somethingWentWrong'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      {
        !leadToken &&
        <>
          <div className={styles['register-form__fields-divider']}>
            <Input
              id={FieldNames.FIRST_NAME}
              name={FieldNames.FIRST_NAME}
              type='text'
              label={t('outerLayout.form.register.labels.firstName')}
              value={fields[FieldNames.FIRST_NAME].value}
              required
              onChange={(e) => { onChangeHandler(FieldNames.FIRST_NAME, e.target.value) }}
              status={fields[FieldNames.FIRST_NAME].isValid === false ? 'error' : 'default'}
              error={fields[FieldNames.FIRST_NAME].errorMsg}
              preventAutoComplete
              disabled={loading}
            />
            <Input
              id={FieldNames.LAST_NAME}
              name={FieldNames.LAST_NAME}
              type='text'
              label={t('outerLayout.form.register.labels.lastName')}
              value={fields[FieldNames.LAST_NAME].value}
              required
              onChange={(e) => { onChangeHandler(FieldNames.LAST_NAME, e.target.value) }}
              status={fields[FieldNames.LAST_NAME].isValid === false ? 'error' : 'default'}
              error={fields[FieldNames.LAST_NAME].errorMsg}
              preventAutoComplete
              disabled={loading}
            />
          </div>
          <Input
            id={FieldNames.EMAIL}
            name={FieldNames.EMAIL}
            type='email'
            label={t('outerLayout.form.labels.emailAddress')}
            labelText={`(${t('outerLayout.form.labels.optional')})`}
            value={fields[FieldNames.EMAIL].value}
            onChange={(e) => { onChangeHandler(FieldNames.EMAIL, e.target.value) }}
            status={fields[FieldNames.EMAIL].isValid === false ? 'error' : 'default'}
            error={fields[FieldNames.EMAIL].errorMsg}
            preventAutoComplete
            disabled={loading}
          />
          <Input
            id={FieldNames.BUSINESS_NAME}
            name={FieldNames.BUSINESS_NAME}
            type='text'
            label={t('outerLayout.form.register.labels.businessName')}
            value={fields[FieldNames.BUSINESS_NAME].value}
            onChange={(e) => { onChangeHandler(FieldNames.BUSINESS_NAME, e.target.value) }}
            status={fields[FieldNames.BUSINESS_NAME].isValid === false ? 'error' : 'default'}
            error={fields[FieldNames.BUSINESS_NAME].errorMsg}
            preventAutoComplete
            disabled={loading}
          />
          <Dropdown label={t('outerLayout.form.register.labels.preferredLang')}>
            <DropdownButton
              type='input'
              text={getRelatedLabel(fields[FieldNames.PREFERRED_LANGUAGE].value as string, preferredLanguages)}
              placeholder={t('outerLayout.form.register.placeholders.selectLanguage')}
            />
            <DropdownMenu>
              {preferredLanguages.map(lang => (
                <DropdownMenuItem
                  text={lang.label}
                  key={`preferred-language-${lang.value}`}
                  onClickHandler={() => onChangeHandler(FieldNames.PREFERRED_LANGUAGE, lang.value)}
                  selected={fields[FieldNames.PREFERRED_LANGUAGE].value === lang.value}
                />
              ))}
            </DropdownMenu>
          </Dropdown>
          {
            country === COUNTRIES.PAK &&
            <>
              <Dropdown label={t('outerLayout.form.register.labels.chooseUrProvince')}>
                <DropdownButton
                  type='input'
                  text={fields[FieldNames.PROVINCE].value}
                  placeholder={t('outerLayout.form.register.placeholders.selectProvince')}
                />
                <DropdownMenu>
                  {pakistanProvinces.map(province => (
                    <DropdownMenuItem
                      text={province.label}
                      key={`pakistan-province-${province.value}`}
                      onClickHandler={() => onChangeHandler(FieldNames.PROVINCE, province.value)}
                      selected={fields[FieldNames.PROVINCE].value === province.value}
                    />
                  ))}
                </DropdownMenu>
              </Dropdown>

              <Dropdown label={t('outerLayout.form.register.labels.chooseUrCity')}>
                <DropdownButton
                  type='input'
                  text={fields[FieldNames.CITY].value}
                  placeholder={t('outerLayout.form.register.placeholders.selectCity')}
                  disabled={!fields[FieldNames.PROVINCE].value}
                />
                <DropdownMenu>
                  {
                    pakistanProvinceBasedCities
                      .find(provincesArr => provincesArr.province === fields[FieldNames.PROVINCE].value)?.cities
                      .map(city => (
                        <DropdownMenuItem
                          text={city.label}
                          key={`pakistan-city-${city.value}`}
                          onClickHandler={() => onChangeHandler(FieldNames.CITY, city.value)}
                          selected={fields[FieldNames.CITY].value === city.value}
                        />
                      ))
                  }
                </DropdownMenu>
              </Dropdown>

              <Dropdown label={t('outerLayout.form.register.labels.businessType')}>
                <DropdownButton
                  type='input'
                  text={fields[FieldNames.BUSINESS_TYPE].value}
                  placeholder={t('outerLayout.form.register.placeholders.selectBussinessType')}
                />
                <DropdownMenu>
                  {
                    mccs.map(mcc => (
                      <DropdownMenuItem
                        text={mcc.label}
                        key={`mcc-${mcc.value}`}
                        onClickHandler={() => onChangeHandler(FieldNames.BUSINESS_TYPE, mcc.value)}
                        selected={fields[FieldNames.BUSINESS_TYPE].value === mcc.value}
                      />
                    ))
                  }
                </DropdownMenu>
              </Dropdown>

              <div className={styles['register-form__checkbox-group']}>
                <Label text={t('outerLayout.form.register.labels.chooseUrProduct')} />
                <div className={styles['register-form__checkbox-group__checkboxes']}>
                  {
                    products.map((product, i) =>
                      <Checkbox
                        key={`register-form-product-${i}`}
                        id={product.apiKey}
                        text={product.label}
                        onChangeHandler={onProductsChange}
                      />
                    )
                  }
                </div>
              </div>
            </>
          }
        </>
      }
      <Input
        id={FieldNames.PASSWORD}
        name={FieldNames.PASSWORD}
        type='password'
        label={t('outerLayout.form.labels.password')}
        value={fields[FieldNames.PASSWORD].value}
        {...(showPasswordRules ? { rules: passwordValidationRules } : {})}
        onChange={(e) => { onChangeHandler(FieldNames.PASSWORD, e.target.value) }}
        status={fields[FieldNames.PASSWORD].isValid === false ? 'error' : 'default'}
        preventAutoComplete
        disabled={loading}
      />
      <Input
        id={FieldNames.CONFIRM_PASSWORD}
        name={FieldNames.CONFIRM_PASSWORD}
        type='password'
        label={t('outerLayout.form.labels.confirmPassword')}
        value={fields[FieldNames.CONFIRM_PASSWORD].value}
        onChange={(e) => { onChangeHandler(FieldNames.CONFIRM_PASSWORD, e.target.value) }}
        status={fields[FieldNames.CONFIRM_PASSWORD].isValid === false ? 'error' : 'default'}
        error={fields[FieldNames.CONFIRM_PASSWORD].errorMsg}
        preventAutoComplete
        disabled={loading}
      />
      <div className='form__submit'>
        {serverErrMsg && <div className='form__submit__server-err'>{serverErrMsg}</div>}

        <Button
          id={SUBMIT_BTN_ID}
          size='md'
          color='primary'
          variant='contained'
          text={loading ? undefined : t('outerLayout.form.register.signUp')}
          leadingIcon={loading ? Spinner : undefined}
          disabled={!isFormValid(fields) || loading}
          onClickHandler={leadToken ? handleLeadsSubmit : handleSubmit}
        />
      </div>
      <div className={styles["register-form__already-have-account"]}>
        <span className={styles["register-form__already-have-account__text"]}>{t('outerLayout.form.register.alreadyHaveAcc')}</span>
        <div className={styles["register-form__already-have-account__action"]}>
          <Button
            size='sm'
            color='primary'
            variant='link'
            text={t('outerLayout.form.register.signIn')}
            onClickHandler={redirectToLogin}
          />
        </div>
      </div>
    </>
  )
}

export default RegisterForm;